import { Link } from 'gatsby';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import React, { useEffect, useRef, useState } from 'react';
import Image from 'src/elements/image/image';
import LinkBranded from 'src/elements/links/Link.branded';
import Heading from 'src/elements/typography/heading';
import { formatElementFromHtmlAst, MarkupElement } from 'src/functions/formatElement.htmlAst';
import useWindowResize from 'src/hooks/useWindowResize';
import {
  CaseStudiesCarouselContainer,
  CaseStudiesSlider,
  CaseStudiesSliderContainer,
  CaseStudy,
  CaseStudyImage,
  CaseStudyTitle,
  ChevronContainer,
  ConfettiWrapper
} from './featured.carousel.styles';

export interface FeaturedCarrouselProps {
  posts: {
    title: string;
    image: IGatsbyImageData;
    featuredDescriptionPreview?: {
      htmlAst: {
        type: string;
        children: MarkupElement[]
      }
    };
    description: {
      htmlAst: {
        type: string;
        children: MarkupElement[]
      }
    }
    slug: string;
  }[]
}

const FeaturedCarousel = ({ posts }: FeaturedCarrouselProps): JSX.Element => {
  const [ slideIndex, setSlideIndex ] = useState<number>(0);
  const [ containerWidth, setContainerWidth ] = useState<number>(0);
  const { width: windowWidth } = useWindowResize();
  const featureContainerlRef = useRef<HTMLDivElement>(null);

  const updateCarouselWidth = () => {
    if (featureContainerlRef) {
      setContainerWidth((featureContainerlRef.current as HTMLDivElement).getBoundingClientRect().width)
    }
  }

  useEffect(() => {
    updateCarouselWidth()
  }, [ windowWidth, updateCarouselWidth ])


  const slideLeft = () => {
    if (slideIndex > 0) {
      setSlideIndex(slideIndex - 1);
    }
  }
  const slideRight = () => {
    if (slideIndex < (posts.length - 1)) {
      setSlideIndex(slideIndex + 1);
    }
  }

  return (

    <ConfettiWrapper>
      <CaseStudiesCarouselContainer>
        <ChevronContainer onClick={slideLeft} style={{ opacity: slideIndex > 0 ? 1 : 0 }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="29" enableBackground="new 0 0 512 512" version="1.1" viewBox="0 0 512 512"><path d="m150.11 242.03 174.54-232.72c7.714-10.283 22.298-12.365 32.582-4.655 10.283 7.713 12.367 22.3 4.655 32.582l-164.07 218.76 164.07 218.77c7.713 10.282 5.627 24.871-4.655 32.582-4.186 3.14-9.086 4.656-13.945 4.656-7.076 0-14.064-3.215-18.637-9.311l-174.54-232.73c-6.205-8.274-6.205-19.653 1e-3 -27.927z" fill="#191F28" />
          </svg>
        </ChevronContainer>
        <CaseStudiesSliderContainer ref={featureContainerlRef}>
          <CaseStudiesSlider style={{ transform: `translateX(-${slideIndex * containerWidth}px)`, opacity: !containerWidth ? 0 : 1 }}>
            {
              posts.map((post, postIndex) => (
                <CaseStudy key={postIndex} style={{ width: `${containerWidth - 40}px` }}>
                  <Link to={`/case-studies/${post.slug}`}>
                    <CaseStudyImage>
                      <Image childImageSharp={post.image} alt={post.title} />
                    </CaseStudyImage>
                  </Link>
                  <CaseStudyTitle>
                    <Link to={`/case-studies/${post.slug}`}>
                      <Heading
                        text={post.title}
                        size="1.5em"
                        weight="normal"
                        align="center"
                      />
                    </Link>
                    {
                      post.featuredDescriptionPreview ?
                        formatElementFromHtmlAst(post.featuredDescriptionPreview.htmlAst.children.find((child) => child.type === 'element'), 'p') :
                        formatElementFromHtmlAst(post.description.htmlAst.children.find((child) => child.type === 'element'), 'p')
                    }
                  </CaseStudyTitle>
                  <LinkBranded
                    text="Read the Story"
                    justify="center"
                    size="1em"
                    url={`/case-studies/${post.slug}`}
                  />
                </CaseStudy>
              ))
            }
          </CaseStudiesSlider>
        </CaseStudiesSliderContainer>
        <ChevronContainer onClick={slideRight} style={{ opacity: 1 + slideIndex >= posts.length ? 0 : 1 }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="29" version="1.1" x="0px" y="0px" viewBox="0 0 512 512" enableBackground="new 0 0 512 512" spacing="preserve"><path fill="#191F28" d="M361.891,242.03L187.347,9.31c-7.714-10.283-22.298-12.365-32.582-4.655 c-10.283,7.713-12.367,22.3-4.655,32.582l164.072,218.758L150.111,474.762c-7.713,10.282-5.627,24.871,4.655,32.582 c4.186,3.14,9.086,4.656,13.945,4.656c7.076,0,14.064-3.215,18.637-9.311l174.544-232.732 C368.097,261.683,368.097,250.304,361.891,242.03z" />
          </svg>
        </ChevronContainer>
      </CaseStudiesCarouselContainer>
    </ConfettiWrapper>
  )
}

export default FeaturedCarousel;
